import { PaginationStore } from "stores/data/pagination.store"
import { computed, makeObservable, observable, transaction } from "mobx"
import { RootStore } from "stores/root.store"
import {
  createGamePublisher,
  getGamePublishers,
  updateGamePublisher,
} from "../api/game-publishers.call"


export interface GamePublisher {
  id: string;
  name: string;
  costReportingPlatform: GameAttributionPlatform
}

export type CreateGamePublisher = Omit<GamePublisher, "id">

export type UpdateGamePublisher = Partial<CreateGamePublisher>;

export const GameAttributionPlatform = {
  ADJUST: "ADJUST",
  APPS_FLYER: "APPS_FLYER",
  SINGULAR: "SINGULAR",
  EVERFLOW: "EVERFLOW",
} as const

export type GameAttributionPlatform = keyof typeof GameAttributionPlatform;

export class GamePublishersStore extends PaginationStore<GamePublisher> {
  @observable
  private _gamePublishers: GamePublisher[] = []
  @observable
  public loadedPages: number = 1
  @observable
  public isEmptyArr: boolean = false

  constructor(rootStore: RootStore) {
    super(rootStore)
    makeObservable(this)
  }

  public async getData(): Promise<GamePublisher[]> {
    return this.getGamePublishers()
  }

  public async getGamePublishers() {
    this.resetFuncAfterSuccess()
    if (this._gamePublishers.length === 0) {
      this.loadedPages = 1
    }
    this.isEmptyArr = false

    const gamePublishers = await this.defaultFunction(
      getGamePublishers,
      this.loadedPages,
      100,
    )
    if (gamePublishers) {
      transaction(() => {
        this.isLoading = false
        if (gamePublishers.data?.length === 0 && this.loadedPages === 1) {
          this.isEmptyArr = true
        }
        if (this.loadedPages > 1) {
          this._gamePublishers = [...this._gamePublishers, ...gamePublishers.data]
        } else {
          this._gamePublishers = gamePublishers.data
        }
        this.total = gamePublishers.total
      })

      return gamePublishers.data
    }
  }

  async createGamePublisher(data: CreateGamePublisher) {
    const newPublisher = await this.defaultFunction(createGamePublisher, data)
    if (newPublisher) {
      transaction(() => {
        this._gamePublishers = [newPublisher, ...this._gamePublishers]
        this.total += 1
      })
    }
  }

  async updateGamePublisher(id: string, data: UpdateGamePublisher) {
    const updatedPublisher = await this.defaultFunction(updateGamePublisher, id, data)
    if (updatedPublisher) {
      transaction(() => {
        this._gamePublishers = this._gamePublishers.map(pub => pub.id === id ? updatedPublisher : pub,
        )
      })
    }
  }

  @computed
  get gamePublishers(): GamePublisher[] {
    return this._gamePublishers
  }
}
